<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  props: {
    company: Object,
  },
  setup(props) {
    const { t, locale } = useI18n();
    const store = useStore();
    const userRole = computed(() => store.state.user?.role);

    const getByLang = (field) =>
      computed(() => props.company[field]?.[locale.value] || "");

    const aboutText = getByLang("about");
    const valuesText = getByLang("values");
    const locationsText = getByLang("locations");
    const tagsText = getByLang("tags");

    const dialog = ref(false);
    const bookingDialog = ref(false);
    const snackbar = ref(false);
    const snackbarMessage = ref("");
    const snackbarColor = ref("");
    const selectedTimes = ref([]);
    const isRemoving = ref(false);

    const fixedDate = "2026-11-10";

    const allTimes = computed(() =>
      [...(props.company.availableTimes || [])].sort(),
    );

    const logoUrl = computed(() => {
      if (!props.company.logo?.url) return null;
      const version = props.company.updatedAt || Date.now();
      return `${props.company.logo.url}?v=${version}`;
    });

    const appliedTimes = computed(() =>
      store.getters["meeting/getAppliedTimes"](props.company._id, fixedDate),
    );

    const bookedTimes = computed(() =>
      store.getters["meeting/getBookedTimes"](props.company._id, fixedDate),
    );

    const availableTimes = computed(() =>
      allTimes.value.filter(
        (t) =>
          !appliedTimes.value.includes(t) && !bookedTimes.value.includes(t),
      ),
    );

    const openBookingDialog = () => {
      if (!store.state.token) {
        snackbarMessage.value = t("errors.signedIn");
        snackbarColor.value = "error";
        snackbar.value = true;
        return;
      }
      selectedTimes.value = [];
      isRemoving.value = false;
      bookingDialog.value = true;
    };

    const submitMeetings = async () => {
      try {
        await store.dispatch("meeting/submitMeetings", {
          companyId: props.company._id,
          date: fixedDate,
          times: selectedTimes.value,
        });
        snackbarMessage.value = t("success.contactMeetingApplied");
        snackbarColor.value = "success";
        snackbar.value = true;
        bookingDialog.value = false;
      } catch (err) {
        snackbarMessage.value = `${t("errors.applyTimes")}. ${
          err.response?.data?.message || ""
        }`;
        snackbar.value = true;
        snackbarColor.value = "error";
      }
    };

    const deleteMeetings = async () => {
      try {
        await store.dispatch("meeting/deleteMeetings", {
          companyId: props.company._id,
          date: fixedDate,
          times: selectedTimes.value,
        });
        snackbarMessage.value = t("success.timesRemoved");
        snackbarColor.value = "success";
        snackbar.value = true;
        selectedTimes.value = [];
        isRemoving.value = false;
        bookingDialog.value = false;
      } catch {
        snackbarMessage.value = t("errors.removeTimes");
        snackbarColor.value = "error";
        snackbar.value = true;
      }
    };

    const getChipProps = (time) => {
      const isSelected = selectedTimes.value.includes(time);
      const isApplied = appliedTimes.value.includes(time);
      const isBooked = bookedTimes.value.includes(time);

      if (isBooked) return { disabled: true, variant: "elevated" };

      if (isRemoving.value) {
        return {
          disabled: !isApplied,
          color: isApplied ? (isSelected ? "error" : "error") : "grey",
          variant: isSelected ? "elevated" : "outlined",
        };
      }
      return {
        disabled: isApplied,
        color: isSelected ? "primary" : "primary",
        variant: isSelected ? "elevated" : "outlined",
      };
    };

    watch(isRemoving, () => (selectedTimes.value = []));

    const isInterested = computed(() => {
      const user = store.state.user;
      if (!user || !user.interestedCompanies) return false;
      return user.interestedCompanies.some(
        (c) => c._id === props.company._id || c === props.company._id,
      );
    });

    const handleInterestClick = async () => {
      if (!store.state.token) {
        snackbarMessage.value = t("errors.signedIn");
        snackbarColor.value = "error";
        snackbar.value = true;
        return;
      }
      try {
        if (isInterested.value) {
          await store.dispatch("student/removeInterest", props.company._id);
          snackbarMessage.value = t("company.interestRemoved");
          snackbarColor.value = "success";
        } else {
          await store.dispatch("student/addInterest", props.company._id);
          snackbarMessage.value = t("company.interestSaved");
          snackbarColor.value = "success";
        }
        snackbar.value = true;
      } catch {
        snackbarMessage.value = t("errors.somethingWentWrong");
        snackbarColor.value = "error";
        snackbar.value = true;
      }
    };

    return {
      dialog,
      bookingDialog,
      aboutText,
      valuesText,
      locationsText,
      tagsText,
      snackbar,
      snackbarMessage,
      snackbarColor,
      isInterested,
      handleInterestClick,
      openBookingDialog,
      availableTimes,
      selectedTimes,
      submitMeetings,
      allTimes,
      deleteMeetings,
      isRemoving,
      getChipProps,
      userRole,
      logoUrl,
      t,
    };
  },
};
</script>

<template>
  <v-card class="company-card pa-4 mt-6" outlined>
    <div v-if="company.logo">
      <v-img
        :src="logoUrl"
        aspect-ratio="1"
        cover
        width="250"
        :alt="company.name + ' logo'"
      >
        <template #error>
          <div
            class="d-flex align-center justify-center"
            style="background-color: #eee; height: 100%; width: 100%"
          >
            <v-icon size="40">mdi-image-off</v-icon>
          </div>
        </template>
      </v-img>
    </div>
    <div
      v-else
      class="d-flex align-center justify-center"
      style="background-color: #eee; height: 200px; width: 250px"
    >
      <v-icon size="40">mdi-image-off</v-icon>
    </div>

    <h3 class="text-h6 text-center">{{ company.name }}</h3>
    <div class="custom-chip-container">
      <v-chip
        v-for="(tag, index) in tagsText"
        :key="index"
        class="chip"
        color="primary"
        variant="elevated"
      >
        {{ tag }}
      </v-chip>
    </div>

    <v-row class="mt-4" justify="center">
      <v-col cols="auto" class="d-flex flex-column align-center">
        <v-btn
          v-if="userRole !== 'employee'"
          variant="outlined"
          class="mb-2"
          @click="dialog = true"
        >
          {{ t("company.readMore") }}
        </v-btn>

        <v-btn
          v-if="userRole !== 'employee'"
          class="mb-2"
          :color="isInterested ? 'success' : 'grey'"
          :variant="isInterested ? 'elevated' : 'outlined'"
          @click="handleInterestClick"
        >
          {{ t("company.interested") }}
        </v-btn>

        <v-btn
          v-if="company.contactMeeting && userRole !== 'employee'"
          variant="outlined"
          @click="openBookingDialog()"
        >
          {{ t("company.applyContactMeeting") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h6" style="white-space: normal">{{
          company.name
        }}</v-card-title>
        <v-card-subtitle>
          {{ t("company.founded") }}: {{ company.founded }}<br />
          {{ t("company.employees") }}: {{ company.employees }}<br />
          {{ t("company.locations") }}: {{ locationsText }}
        </v-card-subtitle>

        <v-card-text>
          <p class="text-h6 mt-2">{{ t("company.about") }}</p>
          <p>{{ aboutText }}</p>

          <p class="text-h6 mt-4">{{ t("company.values") }}</p>
          <p>{{ valuesText }}</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false" variant="outlined">{{
            t("common.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Kontaktmöte popup -->
    <v-dialog v-model="bookingDialog" max-width="500">
      <v-card>
        <v-card-title class="d-flex flex-wrap justify-center text-center">
          <span class="me-1">{{ company.name }} –</span>
          <span class="d-none d-sm-inline">{{
            t("company.contactMeeting")
          }}</span>
          <span class="d-inline d-sm-none w-100 text-center">{{
            t("company.contactMeeting")
          }}</span>
        </v-card-title>
        <v-card-text>
          <div class="mt-4 chip-container-centered">
            <p class="text-center mb-6">
              {{ t("company.contactMeetingCardInfo") }}
            </p>
            <b class="text-error text-center mb-6">
              {{ t("company.applyingNote") }}
            </b>
            <p class="text-center">
              {{
                isRemoving ? t("company.removeTimes") : t("company.chooseTimes")
              }}
              2026-11-10
            </p>
            <v-chip-group
              v-if="availableTimes.length > 0"
              v-model="selectedTimes"
              multiple
            >
              <div class="chip-grid">
                <v-chip
                  v-for="time in allTimes"
                  :key="time"
                  :value="time"
                  v-bind="getChipProps(time)"
                  style="min-height: 40px"
                >
                  {{ time }}
                </v-chip>
              </div>
            </v-chip-group>
            <div v-else>
              {{ t("company.noTimes") }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-row class="w-100" dense>
            <v-col cols="12" sm="auto">
              <v-btn
                v-if="!isRemoving"
                :disabled="selectedTimes.length === 0"
                @click="submitMeetings"
                color="primary"
                variant="outlined"
                class="fixed-width-btn"
                block
              >
                {{ t("common.apply") }}
              </v-btn>

              <v-btn
                v-else
                :disabled="selectedTimes.length === 0"
                @click="deleteMeetings"
                color="error"
                variant="outlined"
                class="fixed-width-btn"
                block
              >
                {{ t("company.removeTimes") }}
              </v-btn>
            </v-col>

            <v-col cols="12" sm="auto">
              <v-btn
                @click="isRemoving = !isRemoving"
                color="warning"
                variant="outlined"
                class="fixed-width-btn"
                block
              >
                {{
                  isRemoving
                    ? t("company.cancelRemoval")
                    : t("company.removeAppliedTimes")
                }}
              </v-btn>
            </v-col>
            <v-spacer />
            <v-col cols="12" sm="auto">
              <v-btn
                @click="bookingDialog = false"
                color="black"
                variant="outlined"
                class="fixed-width-btn"
                block
              >
                {{ t("common.cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>

  <v-snackbar
    v-model="snackbar"
    :timeout="5000"
    :color="snackbarColor"
    location="top"
    class="mt-12"
  >
    {{ snackbarMessage }}
  </v-snackbar>
</template>

<style scoped>
.company-card {
  width: 100%;
  max-width: 350px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.custom-chip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  justify-content: center;
}

.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover {
  /*FUNGERAR SÅDÄR PÅ TELEFON*/
  /*background-color: #763493 !important;*/
  /*color: white !important;*/
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.active-btn {
  /*background-color: white !important;*/
  color: #763493 !important;
  font-weight: bold;
}

.chip-container-centered {
  display: flex;
  flex-direction: column;
  align-items: center; /* Horisontellt centrerad */
}

.chip-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); /* 3 chips per rad */
  gap: 8px;
}

@media (max-width: 600px) {
  .chip-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 600px) {
  .fixed-width-btn {
    min-width: 125px;
  }
}
</style>
